/**
 * Normalizes optional `professionalSuggestion` and `budgetAssessment` on `clientAnalysis`.
 * See docs/admin-email-analysis-professional-and-budget.md
 */

/**
 * Resolves analysis fields for one email-analysis **history** row.
 * Backend may either nest everything in `step.clientAnalysis` or flatten the same fields on `step`.
 * Root-level `professionalSuggestion` / `budgetAssessment` are merged on top of nested `clientAnalysis` when both exist.
 *
 * @param {Record<string, unknown> | null | undefined} step
 * @returns {Record<string, unknown> | null}
 */
export function rawClientAnalysisFromEmailAnalysisStep(step) {
  if (!step || typeof step !== "object") return null;
  const nested = step.clientAnalysis && typeof step.clientAnalysis === "object" ? { ...step.clientAnalysis } : null;
  if (nested) {
    const out = { ...nested };
    if (step.professionalSuggestion !== undefined) {
      out.professionalSuggestion = step.professionalSuggestion ?? nested.professionalSuggestion;
    }
    if (step.budgetAssessment !== undefined) {
      out.budgetAssessment = step.budgetAssessment ?? nested.budgetAssessment;
    }
    if (step.analysisSchemaVersion !== undefined) {
      out.analysisSchemaVersion = step.analysisSchemaVersion ?? nested.analysisSchemaVersion;
    }
    return out;
  }
  const hasFlat =
    step.summary != null ||
    step.description != null ||
    step.verdict != null ||
    step.status != null ||
    step.offerSignal != null ||
    step.offerLabel != null ||
    step.professionalSuggestion ||
    step.budgetAssessment;
  if (!hasFlat) return null;
  return {
    status: step.status,
    summary: step.summary,
    description: step.description,
    offerLabel: step.offerLabel,
    offerSignal: step.offerSignal,
    analyzedAt: step.analyzedAt ?? step.analysedAt,
    modelLabel: step.modelLabel,
    confidence: step.confidence,
    verdict: step.verdict,
    sentimentLabel: step.sentimentLabel,
    clientIntent: step.clientIntent,
    concerns: step.concerns,
    replyAngles: step.replyAngles,
    errorMessage: step.errorMessage,
    analysisSchemaVersion: step.analysisSchemaVersion,
    professionalSuggestion: step.professionalSuggestion,
    budgetAssessment: step.budgetAssessment,
  };
}

function nonEmptyStrings(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((x) => String(x ?? "").trim()).filter(Boolean);
}

/**
 * @param {unknown} ca
 * @returns {null | {
 *   recommendedService: string,
 *   whyThisService: string,
 *   requiredProfessionalLevel: string,
 *   requiredSkills: string[],
 *   deliveryApproach: string,
 *   riskIfUnderScoped: string[],
 *   suggestedNextQuestion: string
 * }}
 */
export function normalizeProfessionalSuggestion(ca) {
  const ps = ca?.professionalSuggestion;
  if (!ps || typeof ps !== "object") return null;
  const recommendedService = String(ps.recommendedService ?? "").trim();
  const whyThisService = String(ps.whyThisService ?? "").trim();
  const requiredProfessionalLevel = String(ps.requiredProfessionalLevel ?? "").trim();
  const deliveryApproach = String(ps.deliveryApproach ?? "").trim();
  const suggestedNextQuestion = String(ps.suggestedNextQuestion ?? "").trim();
  const requiredSkills = nonEmptyStrings(ps.requiredSkills);
  const riskIfUnderScoped = nonEmptyStrings(ps.riskIfUnderScoped);
  const has =
    recommendedService ||
    whyThisService ||
    requiredProfessionalLevel ||
    deliveryApproach ||
    suggestedNextQuestion ||
    requiredSkills.length ||
    riskIfUnderScoped.length;
  if (!has) return null;
  return {
    recommendedService,
    whyThisService,
    requiredProfessionalLevel,
    requiredSkills,
    deliveryApproach,
    riskIfUnderScoped,
    suggestedNextQuestion,
  };
}

/**
 * @param {unknown} ca
 * @returns {null | {
 *   status: string,
 *   fitScore: number | null,
 *   estimatedRangeMin: number | null,
 *   estimatedRangeMax: number | null,
 *   currency: string,
 *   clientBudgetMentioned: string | null,
 *   gapNote: string,
 *   negotiationAdvice: string[],
 *   suggestedClientReply: string
 * }}
 */
export function normalizeBudgetAssessment(ca) {
  const b = ca?.budgetAssessment;
  if (!b || typeof b !== "object") return null;
  const status = String(b.status ?? "").trim().toLowerCase();
  const fitScore = typeof b.fitScore === "number" && !Number.isNaN(b.fitScore) ? b.fitScore : null;
  const min = b.estimatedRangeMin != null ? Number(b.estimatedRangeMin) : null;
  const max = b.estimatedRangeMax != null ? Number(b.estimatedRangeMax) : null;
  const currency = String(b.currency ?? "USD").trim() || "USD";
  const clientBudgetMentioned = String(b.clientBudgetMentioned ?? "").trim() || null;
  const gapNote = String(b.gapNote ?? "").trim();
  const negotiationAdvice = nonEmptyStrings(b.negotiationAdvice);
  const suggestedClientReply = String(b.suggestedClientReply ?? "").trim();
  const has =
    status ||
    fitScore != null ||
    (min != null && !Number.isNaN(min)) ||
    (max != null && !Number.isNaN(max)) ||
    clientBudgetMentioned ||
    gapNote ||
    negotiationAdvice.length ||
    suggestedClientReply;
  if (!has) return null;
  return {
    status,
    fitScore,
    estimatedRangeMin: min != null && !Number.isNaN(min) ? min : null,
    estimatedRangeMax: max != null && !Number.isNaN(max) ? max : null,
    currency,
    clientBudgetMentioned,
    gapNote,
    negotiationAdvice,
    suggestedClientReply,
  };
}

/** CSS modifier for budget status pill (detail + thread strip). */
export function budgetAssessmentPillClass(status) {
  const s = String(status ?? "").trim().toLowerCase();
  if (s === "good") return "admin-dash__email-analysis-budget-pill--good";
  if (s === "acceptable") return "admin-dash__email-analysis-budget-pill--acceptable";
  if (s === "low") return "admin-dash__email-analysis-budget-pill--low";
  return "admin-dash__email-analysis-budget-pill--unclear";
}

/** @param {string} status */
export function budgetStatusDisplayLabel(status) {
  const s = String(status ?? "").toLowerCase();
  if (s === "good") return "Good fit";
  if (s === "acceptable") return "Acceptable";
  if (s === "low") return "Tight / low";
  if (s === "unclear") return "Unclear";
  return status ? status.replace(/_/g, " ") : "";
}

/**
 * @param {{ currency?: string, estimatedRangeMin?: number|null, estimatedRangeMax?: number|null }} b
 */
export function formatEstimatedBudgetRange(b) {
  const cur = b.currency || "USD";
  const min = b.estimatedRangeMin;
  const max = b.estimatedRangeMax;
  if (min == null && max == null) return null;
  const fmt = (n) =>
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Math.round(n));
  if (min != null && max != null) return `${cur} ${fmt(min)} – ${fmt(max)}`;
  if (min != null) return `${cur} ${fmt(min)}+`;
  return `up to ${cur} ${fmt(max)}`;
}
